import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { CopyButton } from "~/components/CopyButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/Dialog";
import { Icon } from "~/components/icon";
import { OrgListItem } from "~/components/OrgListItem";
import { SessionExpired } from "~/components/SessionExpired";
import { SlackChannelLink } from "~/components/SlackChannelLink";
import { useAuth } from "~/contexts/auth";
import { useKeybindings } from "~/hooks/useKeyBindings";
import { ClusterOperationsCommandPalette } from "~/routes/_index/components/ClusterOperationsCommandPalette";
import { NamespaceSearchModal } from "~/routes/_index/components/NamespaceSearchModal";
import type { Org, SiblingOrg } from "~/types";
import {
  formatBytes,
  formatNumber,
  getDatadogLogsUrl,
  getDatadogOrgDashboardUrl,
  getDatadogTracesUrl,
  getWorkOSUrl,
} from "~/utils";
import { formatRelativeUtc } from "~/utils/time";

function SiblingOrgs({ siblings }: { siblings: SiblingOrg[] }) {
  if (!siblings?.length) return null;

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700">Sibling Orgs</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {siblings.map((sibling) => (
          <OrgListItem
            key={sibling.id}
            id={sibling.id}
            name={sibling.name}
            isByoc={sibling.is_byoc}
            href={`/orgs/${sibling.id}`}
            customerId={sibling.customer_id}
          />
        ))}
      </div>
    </div>
  );
}

function RegionStats({ stats }: { stats: Org["region_stats"] }) {
  if (!stats?.length) return null;

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700">Clusters</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {stats.map((region) => (
          <a
            key={region.region}
            href={`/clusters/${region.region}`}
            className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-100 px-3 py-2 transition-colors hover:bg-gray-200"
          >
            <div className="flex flex-col">
              <span className="font-mono text-sm font-medium text-gray-900">
                {region.region}
              </span>
              <span className="text-xs text-gray-500">
                {region.namespace_count} ns |{" "}
                {formatNumber(region.total_vectors)} vectors |{" "}
                {formatBytes(region.total_bytes)}
              </span>
            </div>
            <Icon name="ChevronRight" size={14} className="text-gray-400" />
          </a>
        ))}
      </div>
    </div>
  );
}

function OrgPurgeNotice({ org }: { org: Org }) {
  if (!org.purge_at) return null;

  const purgeAt = new Date(org.purge_at);
  if (purgeAt > new Date()) return null;

  if (!org.has_namespaces) return null;

  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <div className="font-medium">This org is due for purge.</div>
      <div className="mt-1">
        Purge timestamp was {formatRelativeUtc(purgeAt, new Date())} (
        <span className="font-mono">{purgeAt.toISOString()}</span>). Use{" "}
        <Link to="/orgs-purge" className="font-medium underline">
          Orgs to Purge
        </Link>{" "}
        to review and create purge jobs.
      </div>
    </div>
  );
}

function OrgDetailsCard({ org }: { org: Org }) {
  const [nsModalOpen, setNsModalOpen] = useState(false);
  const aggregateStats = useMemo<{
    namespace_count: number;
    total_vectors: number;
    total_bytes: number;
    last_active: string | null;
  } | null>(() => {
    if (!org || !org.region_stats) return null;
    const regionStats = org.region_stats ?? [];
    const namespace_count = regionStats.reduce(
      (sum, r) => sum + r.namespace_count,
      0,
    );
    const total_vectors = regionStats.reduce(
      (sum, r) => sum + r.total_vectors,
      0,
    );
    const total_bytes = regionStats.reduce((sum, r) => sum + r.total_bytes, 0);
    const last_active =
      regionStats
        .map((r) => r.last_active)
        .filter((d): d is string => d !== null)
        .sort()
        .reverse()[0] ?? null;
    return { namespace_count, total_vectors, total_bytes, last_active };
  }, [org]);
  const displayedClusterRegions = useMemo(
    () => (org.region_stats ?? []).map((stats) => stats.region),
    [org.region_stats],
  );

  if (!aggregateStats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">Failed to fetch org</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span>{org.name}</span>
            {org.is_byoc ? (
              <span className="inline-flex h-6 items-center rounded-full bg-blue-100 px-2 text-xs font-medium leading-none text-blue-700">
                BYOC
              </span>
            ) : (
              <span className="inline-flex h-6 items-center rounded-full bg-green-100 px-2 text-xs font-medium leading-none text-green-700">
                SaaS
              </span>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <OrgPurgeNotice org={org} />

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Org ID
              </div>
              <div className="mt-1 flex items-center gap-2 font-mono text-sm text-gray-900">
                {org.id}
                <CopyButton value={org.id} label="Copy org ID" />
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                WorkOS ID
              </div>
              <div className="mt-1 font-mono text-sm text-gray-900">
                {org.workos_id}
              </div>
            </div>

            {org.customer_id ? (
              <div className="rounded-lg bg-gray-50 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Customer
                </div>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <a
                    href={`/customers/${org.customer_id}`}
                    className="text-sm font-semibold underline underline-offset-2 hover:text-foreground/80"
                  >
                    {org.customer_name ?? "Unknown customer"}
                  </a>
                  <span className="font-mono text-xs text-gray-500">
                    {org.customer_id}
                  </span>
                </div>
              </div>
            ) : null}

            {org.customer_slack_channel ? (
              <div className="rounded-lg bg-gray-50 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Slack Channel
                </div>
                <SlackChannelLink channel={org.customer_slack_channel} />
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Namespaces
              </div>
              {aggregateStats.namespace_count > 0 ? (
                <button
                  onClick={() => setNsModalOpen(true)}
                  className="mt-1 text-sm font-semibold underline underline-offset-2 hover:text-foreground/80"
                >
                  {aggregateStats.namespace_count.toLocaleString()}
                </button>
              ) : (
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  {aggregateStats.namespace_count.toLocaleString()}
                </div>
              )}
            </div>

            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Documents
              </div>
              <div className="mt-1 text-sm font-semibold text-gray-900">
                {formatNumber(aggregateStats.total_vectors)}
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Storage
              </div>
              <div className="mt-1 text-sm font-semibold text-gray-900">
                {formatBytes(aggregateStats.total_bytes)}
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Stats Updated
              </div>
              <div className="mt-1 text-sm text-gray-900">
                {aggregateStats.last_active
                  ? formatRelativeUtc(
                      new Date(aggregateStats.last_active),
                      new Date(),
                    )
                  : "No snapshot"}
              </div>
            </div>
          </div>

          <RegionStats stats={org.region_stats} />

          <SiblingOrgs siblings={org.siblings} />

          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-700">Links</h3>
            <div className="flex flex-wrap gap-2">
              <a
                href={getDatadogOrgDashboardUrl(org.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 transition-colors hover:bg-purple-100"
              >
                <Icon name="Dog" size={16} />
                Dashboard
              </a>
              <a
                href={getDatadogLogsUrl(org.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 transition-colors hover:bg-purple-100"
              >
                <Icon name="Dog" size={16} />
                Logs
              </a>
              <a
                href={getDatadogTracesUrl(org.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 transition-colors hover:bg-purple-100"
              >
                <Icon name="Dog" size={16} />
                Traces
              </a>
              <a
                href={getWorkOSUrl(org.workos_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-200"
              >
                <Icon name="ExternalLink" size={16} className="text-blue-600" />
                WorkOS
              </a>
            </div>
          </div>
        </div>
      </CardContent>

      <Dialog open={nsModalOpen} onOpenChange={setNsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Search Namespaces</DialogTitle>
            <DialogDescription>
              Namespaces for org <span className="font-mono">{org.id}</span>
            </DialogDescription>
          </DialogHeader>
          <NamespaceSearchModal
            orgId={org.id}
            displayedClusterRegions={displayedClusterRegions}
            onSuccess={() => setNsModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function OrgDetails() {
  const [org, setOrg] = useState<Org | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { status, setIsAuthorized } = useAuth();

  // Cmd+K to open command palette
  useKeybindings(
    useMemo(
      () => ({
        "$mod+k": (e: KeyboardEvent) => {
          e.preventDefault();
          setIsCommandPaletteOpen(true);
        },
      }),
      [],
    ),
    {
      enabled: !isCommandPaletteOpen,
    },
  );

  const fetchOrg = useCallback(
    async (orgId: string) => {
      setError(null);
      try {
        const data = await api.getOrg(orgId);
        setOrg(data);
        setError(null);
      } catch (err: any) {
        if (err.status === 401) setIsAuthorized(false);
        setError(err.message ?? "Failed to fetch org");
      }
    },
    [setIsAuthorized],
  );

  useEffect(() => {
    if (orgId) {
      fetchOrg(orgId);
    }
  }, [orgId, fetchOrg]);

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
          <Button className="mt-4" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!org) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading organization details...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <OrgDetailsCard org={org} />

      <ClusterOperationsCommandPalette
        isOpen={isCommandPaletteOpen}
        setIsOpen={setIsCommandPaletteOpen}
        clusters={[]}
      />
    </>
  );
}
